import { Download } from "lucide-react"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { InlineField } from "@/components/ui/inline-edit"
import { Input } from "@/components/ui/input"
import { StatusBadge } from "@/components/studio/StatusBadge"
import { useInlineRecord } from "@/hooks/use-inline-record"
import { inlinePatch } from "@/lib/inline-save"
import { formatSpend, type StudioAdVariant } from "@/components/studio/types"

const TEXTAREA_CLASS =
  "w-full rounded-md border border-hairline bg-page px-3 py-2 text-sm text-ink-body placeholder:text-ink-muted focus:outline-none focus:ring-2 focus:ring-accent"

export function AdVariantCard({ variant }: { variant: StudioAdVariant }) {
  const editable = variant.status === "draft" || variant.status === "ready"

  // The copy is one JSON column, so a tick sends the whole of it with the one
  // line replaced — never a neighbouring box that is still being written.
  const fields = useInlineRecord({
    record: {
      headline: variant.copy.headline ?? "",
      primary_text: variant.copy.primary_text ?? "",
    },
    save: (key, value) =>
      inlinePatch(`/admin/studio/ad-variants/${variant.id}`, {
        ad_variant: { copy: { ...variant.copy, [key]: value } },
      }),
    enabled: editable,
  })
  const headlineState = fields.state("headline")
  const primaryState = fields.state("primary_text")

  return (
    <div className="rounded-lg border border-hairline bg-surface p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Badge tone="accent">{variant.angle.replace(/_/g, " ")}</Badge>
        <StatusBadge status={variant.status} />
      </div>

      {variant.image_url && (
        <img
          src={variant.image_url}
          alt={`Ad image — ${variant.angle.replace(/_/g, " ")}`}
          className="mt-3 w-full rounded-md border border-hairline"
        />
      )}

      {variant.error_message && (
        <p className="mt-2 text-sm text-ink-muted">{variant.error_message}</p>
      )}

      {editable ? (
        <div className="mt-3 flex flex-col gap-2">
          <InlineField label="Headline" htmlFor={`headline-${variant.id}`} state={headlineState}>
            <Input
              id={`headline-${variant.id}`}
              value={headlineState.value}
              onChange={(e) => fields.set("headline", e.target.value)}
              onKeyDown={headlineState.keys}
              className="mt-1"
            />
          </InlineField>
          <InlineField label="Primary text" htmlFor={`primary-${variant.id}`} state={primaryState}>
            <textarea
              id={`primary-${variant.id}`}
              rows={3}
              value={primaryState.value}
              onChange={(e) => fields.set("primary_text", e.target.value)}
              onKeyDown={primaryState.multilineKeys}
              className={`mt-1 ${TEXTAREA_CLASS}`}
            />
          </InlineField>
        </div>
      ) : (
        <div className="mt-3">
          {variant.copy.headline && (
            <p className="text-sm text-ink-body">
              <strong>{variant.copy.headline}</strong>
            </p>
          )}
          {variant.copy.primary_text && (
            <p className="mt-1 text-sm text-ink-body">{variant.copy.primary_text}</p>
          )}
        </div>
      )}

      {variant.insight && (
        <p className="mt-3 text-xs text-ink-muted">
          {variant.insight.impressions.toLocaleString()} impressions ·{" "}
          {variant.insight.ctr !== null ? `${variant.insight.ctr.toFixed(2)}% CTR` : "CTR —"} ·{" "}
          {formatSpend(variant.insight.spend_cents, variant.insight.currency)} spent ·{" "}
          {variant.insight.results} results
        </p>
      )}

      {variant.download_url && (
        <div className="mt-3">
          <Button type="button" size="sm" variant="ghost" asChild>
            <a href={variant.download_url} download className="no-underline">
              <Download className="mr-1.5 h-3.5 w-3.5" />
              Download image
            </a>
          </Button>
        </div>
      )}
    </div>
  )
}
